import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';

/**
 * `03.Pagos` — persisted record of the medios de pago configuration for
 * an accepted venta. One row per payment method the vendor has selected,
 * with its monto, tipo de cambio and mode (Pago Único / Dist. de Pagos).
 *
 * Not a log of individual receipts — that's `04.ComprobantePagos`.
 * Rows are soft-cancelled (Status_P = 'Inactivo') rather than deleted.
 */
export type PagosFields = {
  Title?: string | null;
  IDOperacion_P?: string | null;
  MedioPago_P?: string | null;
  Monto_P?: string | null;
  MontoDolar_P?: string | null;
  TipoCambio_P?: string | null;
  PagoUnico_P?: string | null;
  MonedaOperacion_P?: 'ARS' | 'MXN' | 'USD' | string | null;
  Cliente_P?: string | null;
  Cuotas_P?: string | null;
  ComprobanteSubido_P?: string | null;
  FormatoComprobante_P?: string | null;
  Status_P?: 'Activo' | 'Inactivo' | string | null;
  Fecha_P?: string | null;
  FechaCobro_P?: string | null;
  FechaPago_P?: string | null;
  Mes_P?: string | null;
  Anio_P?: string | null;
  MesAnio_P?: string | null;
  Hora_P?: string | null;
};

export type PagoItem = ListItem<PagosFields>;

export const pagosClient = new SharePointListClient<PagosFields>('Pagos');

/**
 * List every Pagos row for a given operación. We fetch by the indexed
 * `IDOperacion_P` filter only (Status_P isn't indexed on this list) and
 * apply the active-only cut client-side.
 */
export async function listPagosByOperacion(
  operacionId: string | number,
): Promise<PagoItem[]> {
  const items = await pagosClient.listItems({
    filter: `fields/IDOperacion_P eq ${odataEscape(String(operacionId))}`,
  });
  return items.sort((a, b) => Number(a.id) - Number(b.id));
}

export async function listActivePagosByOperacion(
  operacionId: string | number,
): Promise<PagoItem[]> {
  const all = await listPagosByOperacion(operacionId);
  // Legacy SP rows used Status_P='Activo'; the Postgres model carries
  // 'Pendiente' / 'Confirmado' / 'Anulado'. Both worlds agree that
  // anything ≠ 'Anulado' is the live set, so anchor the filter to that
  // (mirrors the comprobantePagos.ts pattern).
  return all.filter((p) => (p.fields.Status_P ?? '') !== 'Anulado');
}
